/**
 * TodoTypeIcon — the type glyphs of the CD-25 Todo container.
 *
 * Ported from the Claude Design handoff (`TodoTypeIcon.dc.html`): 1.6px-stroke
 * line glyphs on `currentColor`, sized to their tile. The type→icon mapping is
 * the design's; the drawing is redone against the app's own inline-SVG
 * convention (the repo has no icon library — every surface inlines its set).
 *
 * The tile treatment is `typeTileClass()`: three categories from the design —
 * `neutral` (paper-2 + inset hairline), `indigo` (indigo-soft), `paprika`
 * (paprika-soft). Category comes from the type, NOT from the caller, so every
 * surface tints a given type identically.
 */
import React from 'react';
import { todoTypeDef, type TodoType } from '../shared/todoTypes';
import styles from './todoContainer.module.css';

const S = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Keyed by the canonical types (legacy values resolve through todoTypeDef). */
const GLYPH: Record<TodoType, React.FC> = {
  todo: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.7" {...S}><polyline points="3,8.5 6.5,11.5 13,4.5" /></svg>
  ),
  text: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <rect x="2.2" y="2.8" width="11.6" height="8" rx="2" /><path d="M5 10.8 L5 13.4 L7.8 10.8" />
      <line x1="5" y1="5.6" x2="11" y2="5.6" /><line x1="5" y1="7.8" x2="9" y2="7.8" />
    </svg>
  ),
  email: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <rect x="1.8" y="3.3" width="12.4" height="9.4" rx="1.6" /><polyline points="2.3,4.8 8,9 13.7,4.8" />
    </svg>
  ),
  // A handset, deliberately NOT a calendar: this to-do PLACES a call, it does
  // not send a booking link. The glyph is half the point of promoting the type.
  phone: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <path d="M5.6 2.6 C5.9 2.4 6.3 2.5 6.5 2.8 L7.7 4.7 C7.9 5 7.8 5.4 7.5 5.6 L6.5 6.3 C6.9 7.6 8.4 9.1 9.7 9.5 L10.4 8.5 C10.6 8.2 11 8.1 11.3 8.3 L13.2 9.5 C13.5 9.7 13.6 10.1 13.4 10.4 L12.5 11.7 C12.2 12.1 11.7 12.3 11.2 12.2 C7.6 11.6 4.4 8.4 3.8 4.8 C3.7 4.3 3.9 3.8 4.3 3.5 Z" />
    </svg>
  ),
  schedule_call: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <rect x="2.4" y="3" width="11.2" height="10.6" rx="1.7" /><line x1="2.4" y1="6" x2="13.6" y2="6" />
      <line x1="5.2" y1="1.6" x2="5.2" y2="4" /><line x1="10.8" y1="1.6" x2="10.8" y2="4" />
      <circle cx="8" cy="9.6" r="0.4" fill="currentColor" stroke="none" />
    </svg>
  ),
  // The cube — the same mark the Build Block Scheduler wears in its own head,
  // so a build to-do reads as the same thing in the list, in the modal and in
  // the scheduler it opens.
  schedule_build: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <path d="M14 10.7V5.3L8 2.3 2 5.3v5.4l6 3z" />
      <path d="M2.2 5.2 8 8.2l5.8-3" />
      <line x1="8" y1="8.2" x2="8" y2="13.7" />
    </svg>
  ),
  create_workflow: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <circle cx="4" cy="4" r="1.7" /><circle cx="4" cy="12" r="1.7" /><path d="M4 5.7 V10.3" />
      <path d="M5.7 4 H9.5 a2 2 0 0 1 2 2 V7.2" />
      <line x1="11.5" y1="9" x2="11.5" y2="13" /><line x1="9.5" y1="11" x2="13.5" y2="11" />
    </svg>
  ),
  edit_workflow: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <circle cx="4" cy="4" r="1.7" /><circle cx="4" cy="12" r="1.7" /><path d="M4 5.7 V10.3" />
      <path d="M5.7 4 H8" /><path d="M8.5 13 L13 8.5 L14.4 9.9 L9.9 14.4 L8 14.4 Z" />
    </svg>
  ),
  send_form: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <rect x="3" y="2" width="10" height="12" rx="1.6" />
      <line x1="5.4" y1="5.4" x2="10.6" y2="5.4" /><line x1="5.4" y1="8" x2="10.6" y2="8" />
      <line x1="5.4" y1="10.6" x2="8.6" y2="10.6" />
    </svg>
  ),
  create_proposal: () => (
    <svg viewBox="0 0 16 16" width="62%" height="62%" strokeWidth="1.4" {...S}>
      <path d="M3.5 2.5 h5.2 l3.8 3.8 V13 a0.6 0.6 0 0 1-0.6 0.6 H4.1 a0.6 0.6 0 0 1-0.6-0.6 Z" />
      <polyline points="8.6,2.6 8.6,6.4 12.4,6.4" />
      <line x1="5.6" y1="9" x2="10.4" y2="9" /><line x1="5.6" y1="11" x2="9" y2="11" />
    </svg>
  ),
  // The AI sparkle is deliberately NOT the paprika ⚡ — that glyph stays
  // exclusive to the automation indicator (see todoTypes.ts).
  ai_action: () => (
    <svg viewBox="0 0 16 16" width="66%" height="66%" fill="currentColor" stroke="none">
      <path d="M8 1 L9.3 6.7 L15 8 L9.3 9.3 L8 15 L6.7 9.3 L1 8 L6.7 6.7 Z" />
      <path d="M13 1.5 L13.6 3.4 L15.5 4 L13.6 4.6 L13 6.5 L12.4 4.6 L10.5 4 L12.4 3.4 Z" />
    </svg>
  ),
};

/** The design's three tile categories, by type. */
export function todoTypeCategory(type: string | null | undefined): 'neutral' | 'indigo' | 'paprika' {
  const value = todoTypeDef(type).value;
  if (value === 'ai_action') return 'paprika';
  if (value === 'todo' || value === 'edit_workflow') return 'neutral';
  return 'indigo';
}

/** Tile class for a given type — pair with a size class (`tile34`/`tile20`). */
export function todoTypeTileClass(type: string | null | undefined): string {
  const cat = todoTypeCategory(type);
  return cat === 'paprika' ? styles.tilePaprika : cat === 'neutral' ? styles.tileNeutral : styles.tileIndigo;
}

const TodoTypeIcon: React.FC<{ type: string | null | undefined }> = ({ type }) => {
  const Glyph = GLYPH[todoTypeDef(type).value] ?? GLYPH.todo;
  return <Glyph />;
};

export default TodoTypeIcon;
